import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight mb-3">{title}</h2>
    <div className="text-white/65 leading-relaxed text-[15px] space-y-3">{children}</div>
  </section>
);

const LegalLayout = ({ title, lastUpdated, children, testid }) => (
  <div className="min-h-screen bg-[#050505] text-white" data-testid={testid}>
    <header className="border-b border-white/5 sticky top-0 z-40 bg-[#050505]/70 backdrop-blur-xl">
      <div className="max-w-[900px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <ArrowLeft className="w-4 h-4 text-white/60" />
          <Logo />
        </Link>
      </div>
    </header>
    <main className="max-w-[820px] mx-auto px-6 lg:px-12 py-16">
      <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-3">// Legal</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-2">{title}</h1>
      <p className="text-white/40 font-mono-c text-xs mb-12">Last updated: {lastUpdated}</p>
      {children}
      <div className="mt-16 pt-8 border-t border-white/5 text-sm text-white/45">
        Questions? Reach us at <a href="mailto:gigafloatapp@gmail.com" className="text-[#00FF88]">gigafloatapp@gmail.com</a>.
      </div>
    </main>
  </div>
);

export const Privacy = () => (
  <LegalLayout title="Privacy Policy" lastUpdated="May 2026" testid="privacy-page">
    <Section title="Who we are">
      <p>GigAfloat (&quot;we&quot;, &quot;us&quot;) is a fintech company in development, building tools to help gig workers smooth income gaps between payouts. This Privacy Policy describes how we handle information you share with us through our waitlist and website (gigafloat.app).</p>
    </Section>

    <Section title="What we collect">
      <p>When you join the waitlist, we collect:</p>
      <ul className="list-disc list-inside space-y-1.5 pl-2">
        <li><strong className="text-white/85">Contact info:</strong> name, email address, phone number (optional)</li>
        <li><strong className="text-white/85">Work profile:</strong> primary gig platform, work frequency, weekly earnings range</li>
        <li><strong className="text-white/85">Product research:</strong> ideal advance amount, top financial challenge, urgency, membership interest, financial gap frequency, expected usage frequency</li>
        <li><strong className="text-white/85">Attribution:</strong> referral source, UTM parameters, device type, user-agent string, referring URL</li>
        <li><strong className="text-white/85">Timestamps:</strong> when you signed up and any subsequent updates</li>
      </ul>
      <p className="text-white/55 italic">We do <strong>not</strong> collect Social Security Numbers, bank account credentials, dates of birth, government ID, or any sensitive financial documentation. This is a waitlist — not a lending application.</p>
    </Section>

    <Section title="How we use it">
      <ul className="list-disc list-inside space-y-1.5 pl-2">
        <li>Notify you when GigAfloat opens for early access</li>
        <li>Send product updates, marketing, and occasional SMS announcements (only with your consent)</li>
        <li>Aggregate, anonymized analysis to inform product development and investor reporting</li>
        <li>Customer support and account communication</li>
      </ul>
      <p>We never sell your personal data. We do not share your contact info with third parties for their marketing.</p>
    </Section>

    <Section title="Service providers">
      <p>We use trusted vendors to operate the platform — including email delivery (Resend), cloud hosting, and analytics. They process data only on our behalf and are contractually bound to protect it.</p>
    </Section>

    <Section title="Your choices">
      <ul className="list-disc list-inside space-y-1.5 pl-2">
        <li>Unsubscribe from emails any time via the link at the bottom of every message</li>
        <li>Reply STOP to any SMS to opt out</li>
        <li>Email <a href="mailto:gigafloatapp@gmail.com" className="text-[#00FF88]">gigafloatapp@gmail.com</a> to request access, correction, or deletion of your data</li>
      </ul>
    </Section>

    <Section title="Security">
      <p>Data is transmitted over HTTPS and stored encrypted at rest. Access is restricted to a minimal set of authorized GigAfloat personnel.</p>
    </Section>

    <Section title="Children">
      <p>GigAfloat is not directed to anyone under 18. We do not knowingly collect data from minors.</p>
    </Section>

    <Section title="Changes">
      <p>We may update this policy as we evolve. Material changes will be communicated via email or on this page.</p>
    </Section>
  </LegalLayout>
);

export const Terms = () => (
  <LegalLayout title="Terms of Service" lastUpdated="May 2026" testid="terms-page">
    <Section title="Acceptance">
      <p>By joining the GigAfloat waitlist or using gigafloat.app, you agree to these Terms. If you do not agree, please do not use the site.</p>
    </Section>

    <Section title="What GigAfloat is — and isn't (yet)">
      <p><strong className="text-white/85">GigAfloat is currently in development.</strong> Joining the waitlist does <strong>not</strong> guarantee approval, funding, or any financial services. We are gathering interest and product feedback to build a future product.</p>
      <p>We make no representations or warranties that GigAfloat will launch, that you will be approved, or that any specific features or pricing will be offered.</p>
    </Section>

    <Section title="Marketing communications">
      <p>By providing your email address (and phone number, if applicable), and checking the consent box, you agree to receive product updates, marketing emails, and occasional text messages from GigAfloat. Message and data rates may apply. Reply STOP at any time to unsubscribe from SMS, or use the unsubscribe link in any email.</p>
    </Section>

    <Section title="Your conduct">
      <p>You agree not to: provide false information; impersonate others; attempt to disrupt or exploit the service; scrape, harvest, or otherwise misuse data on the site.</p>
    </Section>

    <Section title="Intellectual property">
      <p>The GigAfloat brand, logo, copy, and design are owned by GigAfloat. You may not use them without written permission, except as expressly permitted (e.g., personal social sharing of public site content).</p>
    </Section>

    <Section title="Disclaimer of warranties">
      <p>The site and waitlist are provided &quot;as is&quot; without warranty of any kind. We do not guarantee uninterrupted access, accuracy, or any specific outcome from joining the waitlist.</p>
    </Section>

    <Section title="Limitation of liability">
      <p>To the maximum extent permitted by law, GigAfloat is not liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the site or waitlist.</p>
    </Section>

    <Section title="Governing law">
      <p>These Terms are governed by the laws of the United States, without regard to conflict-of-law principles.</p>
    </Section>

    <Section title="Contact">
      <p>Questions about these Terms? Email <a href="mailto:gigafloatapp@gmail.com" className="text-[#00FF88]">gigafloatapp@gmail.com</a>.</p>
    </Section>
  </LegalLayout>
);

export default Privacy;
